import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronDown,
  ChevronUp,
  Trash2,
  Bell,
  BellOff,
  Pencil,
  Plus,
} from "lucide-react";

const CONGESTION_STYLES = {
  Uncongested: { bg: "#16a34a", color: "#fff" },
  Light: { bg: "#4ade80", color: "#111" },
  Medium: { bg: "#eab308", color: "#111" },
  Heavy: { bg: "#ef4444", color: "#fff" },
  Unknown: { bg: "#9ca3af", color: "#fff" },
};

const CongestionBadge = ({ level }) => {
  if (!level) return null;
  const style = CONGESTION_STYLES[level] || CONGESTION_STYLES.Unknown;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {level}
    </span>
  );
};

const formatTravelTime = (seconds) => {
  if (!seconds) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return `${hrs}h ${rem}m`;
};

const MyRoutesPanel = ({
  routes,
  onEditRoute,
  onRenameRoute,
  onDeleteRoute,
  onConfigureAlerts,
  onCreateNew,
  editingRouteId,
  loading,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [renamingRouteId, setRenamingRouteId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleStartRename = (route) => {
    setRenamingRouteId(route.routeId);
    setRenameValue(route.displayName);
  };

  const handleConfirmRename = () => {
    if (renameValue.trim()) {
      onRenameRoute(renamingRouteId, renameValue.trim());
    }
    setRenamingRouteId(null);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      onDeleteRoute(deleteConfirm.routeId);
      setDeleteConfirm(null);
    }
  };

  if (!routes || routes.length === 0) {
    return (
      <Card className="px-3 py-1.5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">My Routes</h3>
          {onCreateNew && (
            <button
              className="p-1 opacity-50 hover:opacity-100 hover:text-blue-500 bg-transparent text-inherit border-none cursor-pointer"
              title="Add a new route"
              onClick={onCreateNew}
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-xs opacity-60 mt-1">
          No saved routes yet. Plan a route and click "Save Route" to get
          started.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between px-3 py-1.5">
        <button
          className="flex-1 flex items-center justify-between hover:opacity-80 bg-inherit text-inherit border-none cursor-pointer"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <h3 className="text-sm font-semibold">My Routes ({routes.length})</h3>
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4 opacity-50" />
          ) : (
            <ChevronUp className="h-4 w-4 opacity-50" />
          )}
        </button>
        {onCreateNew && (
          <button
            className="p-1 ml-2 opacity-50 hover:opacity-100 hover:text-blue-500 bg-transparent text-inherit border-none cursor-pointer"
            title="Add a new route"
            onClick={onCreateNew}
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      {!isCollapsed && (
        <div className="divide-y divide-gray-200/50">
          {routes.map((route) => {
            const isEditing = editingRouteId === route.routeId;
            const travelTime = formatTravelTime(route.travelTimeSeconds);

            return (
              <div
                key={route.routeId}
                className={`px-3 py-2 ${
                  isEditing ? "bg-blue-500/10 border-l-2 border-blue-500" : ""
                }`}
              >
                {/* Route row */}
                <div className="flex items-center justify-between gap-2">
                  <button
                    className="flex-1 text-left min-w-0 bg-inherit text-inherit border-none cursor-pointer"
                    onClick={() => onEditRoute(route.routeId)}
                  >
                    {renamingRouteId === route.routeId ? (
                      <div
                        className="flex gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          className="h-7 text-sm"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleConfirmRename();
                            if (e.key === "Escape") setRenamingRouteId(null);
                          }}
                          autoFocus
                        />
                        <Button
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={handleConfirmRename}
                        >
                          Save
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-sm font-medium truncate">
                          {route.displayName}
                        </span>
                        <CongestionBadge level={route.congestionLevel} />
                        {travelTime && (
                          <span className="text-xs opacity-60 shrink-0">
                            {travelTime}
                          </span>
                        )}
                      </div>
                    )}
                  </button>

                  {/* Action buttons */}
                  {renamingRouteId !== route.routeId && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        className="p-1 opacity-50 hover:opacity-100 hover:text-blue-500 bg-transparent text-inherit border-none cursor-pointer"
                        title="Rename"
                        onClick={() => handleStartRename(route)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        className="p-1 opacity-50 hover:opacity-100 hover:text-blue-500 bg-transparent text-inherit border-none cursor-pointer"
                        title={
                          route.noAlerts
                            ? "No alerts, click to configure"
                            : "Alert settings"
                        }
                        onClick={() => onConfigureAlerts(route.routeId)}
                      >
                        {route.noAlerts ? (
                          <BellOff className="h-3.5 w-3.5" />
                        ) : (
                          <Bell className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <button
                        className="p-1 opacity-50 hover:opacity-100 hover:text-red-500 bg-transparent text-inherit border-none cursor-pointer"
                        title="Delete"
                        onClick={() => setDeleteConfirm(route)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Route</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete "{deleteConfirm?.displayName}"? This
          will also remove all alert settings for this route.
        </DialogDescription>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteConfirm}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Delete
          </Button>
        </DialogFooter>
      </Dialog>
    </Card>
  );
};

export default MyRoutesPanel;
